import { useEffect, useRef, useState } from 'react'
import useScrollTriggerContext from '@contexts/scrollTriggerContext'
import useNotificationsContext from '@contexts/notificationsContext'
import { decrementAttr, incrementAttr } from '@utils/postAttr'
import useVirtuosoSnapshot from '@hooks/useVirtuosoSnapshot'
import { POST_DISPLAY } from '@utils/notificationUtils'
import { colors, fontSize, size } from '@styles/theme'
import { useAuthContext } from '@contexts/authContext'
import useDialog from '@hooks/useDialog'
import useScroll from '@hooks/useScroll'
import styled from '@emotion/styled'
import minsAgo from '@utils/minsAgo'
import NotificationsPostListModal from '@components/notifications/NotificationsPostListModal'
import NotificationsTopNav from '@components/notifications/NotificationsTopNav'
import Notification from '@components/notifications/Notification'
import PullToRefresh from 'react-simple-pull-to-refresh'
import { EmptyNotifications } from '@elements/EmptyState'
import { CircularProgress } from '@mui/material'
import { PiSpinnerBold } from 'react-icons/pi'
import { Virtuoso } from 'react-virtuoso'
import EndDot from '@elements/EndDot'

const TopClearfix = styled.div`
  height: ${size(7)};
  width: 100%;
`

const StyledPullToRefresh = styled(PullToRefresh)`
  // Sets the location of the refresh loade animation
  & div.ptr__pull-down {
    margin-top: ${size(7)};
  }
`

const ReloaderContainer = styled.div`
  position: fixed;
  top: ${size(7)};
  padding: ${size(1.25)};
  width: 100%;
`

const ReloadingLoader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  width: fit-content;
  margin: 0 auto;
  padding: ${size(1)} ${size(1.5)};
  border-radius: 100px;

  font-size: ${fontSize('xs')};
  background-color: ${colors.statusColors.right_now('primary')};
`

const Notifications = () => {
  const { authenticated, state: authState } = useAuthContext()
  const isAnonymous = authState?.user?.isAnonymous
  const [isPullRefreshing, setIsPullRefreshing] = useState(false)
  const [isPostsOpen, openPosts, closePosts] = useDialog('post_notifications_list', 'post-notifications-list-open')
  // Local Scroll Trigger
  const [scrollTarget, setScrollTarget] = useState(undefined)
  // Sets reference to manage top and bottom bar animations
  const { setScrollRef } = useScroll(setScrollTarget, 500)
  // Global Scroll Trigger
  const { scrollTrigger, useScrollTriggerEffect } = useScrollTriggerContext()
  useScrollTriggerEffect(scrollTarget)

  // Causes top and bottom bar to come back into view when we swipe to another conversation
  useEffect(() => setScrollTarget(false), [])

  // Notification State
  const {
    displayNotifications,
    setDisplayNotifications,
    displayNotificationsRef,
    notifications,
    notificationsLoaded,
    notificationsLoadingDelayed,
    noMoreNotifications,
    getInitialNotifications,
    getMoreNotifications,
    firstRender,
    timeSinceLastLoad,
    isNotificationsReloading,
  } = useNotificationsContext()

  // reference for virtuoso list
  const feedRef = useRef()

  //// Virtuoso Snapshot State
  const {
    restoreStateProp,
  } = useVirtuosoSnapshot(
    'notifications-feed-snapshot',
    firstRender,
    feedRef,
    scrollTarget,
  )

  useEffect(() => {
    if (authenticated && !isAnonymous) {
      // Get new notifications on mount if 5 mins have passed?
      if (timeSinceLastLoad < minsAgo(5)) {
        getInitialNotifications()
      }
  
      // On unmount, set all display notifications to "seen_at" with current time
      return () => {
        const currentTime = new Date()
        setDisplayNotifications(
          displayNotificationsRef.current?.map((n) => {
            n.seenAt = currentTime
            return n
          })
        )
      }
    }
  }, [authenticated, isAnonymous])

  const setLikeToState = (post) => {
    const newNotifications = displayNotifications.map((notificationObj) => {
      if (POST_DISPLAY.has(notificationObj.action)) {
        incrementAttr(notificationObj?.post, post.uuid, 'likeCount', (p) => p.currentUserLiked = 1)
      }
      return notificationObj
    })
    setDisplayNotifications(newNotifications)
  }

  const setUnlikeToState = (post) => {
    const newNotifications = displayNotifications.map((notificationObj) => {
      if (POST_DISPLAY.has(notificationObj.action)) {
        decrementAttr(notificationObj?.post, post.uuid, 'likeCount', (p) => p.currentUserLiked = 0)
      }
      return notificationObj
    })
    setDisplayNotifications(newNotifications)
  }

  const setRepostToState = (post) => {
    const newNotifications = displayNotifications.map((notificationObj) => {
      if (POST_DISPLAY.has(notificationObj.action)) {
        incrementAttr(notificationObj?.post, post.uuid, 'repostCount')
      }
      return notificationObj
    })
    setDisplayNotifications(newNotifications)
  }

  const newReplyAfterCreate = (newReply) => {
    const newNotifications = displayNotifications.map((notificationObj) => {
      if (POST_DISPLAY.has(notificationObj.action)) {
        incrementAttr(notificationObj?.post, newReply.parent.uuid, 'replyCount')
      }
      return notificationObj
    })
    setDisplayNotifications(newNotifications)
  }

  const handleRefresh = () => {
    setIsPullRefreshing(true)
    return getInitialNotifications()
      .finally(() => setIsPullRefreshing(false))
  }

  const renderReloadingLoader = () => {
    if (isNotificationsReloading && !isPullRefreshing)
    return (
      <ReloaderContainer>
        <ReloadingLoader>
          <PiSpinnerBold
            size={size(1.5)}
            className='animation_rotation'
          />
          &nbsp;checking new
        </ReloadingLoader>
      </ReloaderContainer>
    )
  }

  return (
    <div style={{ height: '100vh', width: '100vw', position: 'relative' }}>
      <NotificationsPostListModal
        setClosed={closePosts}
        open={isPostsOpen}
      />
      <NotificationsTopNav scrollTrigger={scrollTrigger} />
      <StyledPullToRefresh
        onRefresh={handleRefresh}
        pullingContent=''
        isPullable={notificationsLoaded && !isAnonymous}>
        <Virtuoso
          scrollerRef={node => { if (node) setScrollRef(node) }}
          ref={node => { if (node) feedRef.current = node  }}
          style={{ height: window.innerHeight }}
          data={displayNotifications}
          overscan={window.screen.height*2}
          endReached={() => !noMoreNotifications && getMoreNotifications()}
          atBottomThreshold={window.innerHeight}
          { ...restoreStateProp }
          itemContent={(index, displayNotification) => (
            <>
              {index === 0 && <TopClearfix />}
              <Notification
                key={index}
                displayNotification={displayNotification}
                setLikeToState={setLikeToState}
                setUnlikeToState={setUnlikeToState}
                setRepostToState={setRepostToState}
                newReplyAfterCreate={newReplyAfterCreate}
                openPosts={openPosts}
                index={index}
              />
            </>
          )}
          components={{
            EmptyPlaceholder: () => {
              // TODO: When we refactor this into its own component, we'll probably want to add a delay
              //       So it only renders after a certain number of milliseconds
              if (notificationsLoadingDelayed) {
                return (
                  <div style={{ display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress style={{ marginTop: '10rem', color: '#6A23FF' }} thickness={8} />
                  </div>
                )
              } else if (notificationsLoaded) {
                return (
                  <EmptyNotifications />
                )
              }
            },
            Footer: () => {
              if (notificationsLoaded && displayNotifications?.length > 0) {
                return (
                  <div style={{ padding: `${size(10)} 0` }}>
                    <EndDot />
                  </div>
                )
              }
            },
            Header: () => renderReloadingLoader()
          }}
        />
      </StyledPullToRefresh>
    </div>
  )
}

export default Notifications
